import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useUserPrefs } from '../contexts/UserPrefsContext';
import { HabitItem } from '../components/HabitItem';
import { HabitFormModal } from '../components/HabitFormModal';
import { EmptyState } from '../components/EmptyState';
import { SortableList } from '../components/SortableList';
import { SortableRow } from '../components/SortableRow';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Plus, Sun, Sunset, Moon, Sparkles, ListTodo, Eye, EyeOff, LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';
import { todayISO, fmtPretty, isScheduledOn } from '../lib/dates';
import { calculateStreaks } from '../lib/streaks';
import { enqueueLogUpsert, isOnline } from '../lib/offlineQueue';
import { maybeFireMilestoneEmail } from '../lib/milestones';
import { updateWidgetSnapshot } from '../lib/widget';
import { quoteForDay } from '../lib/quotes';
import { WidgetCoach } from '../components/WidgetCoach';
import { rescheduleHabitReminders } from '../lib/localNotifications';

const GROUPS = [
  { key: 'morning', label: 'Morning', icon: Sun },
  { key: 'afternoon', label: 'Afternoon', icon: Sparkles },
  { key: 'evening', label: 'Evening', icon: Moon },
  { key: 'anytime', label: 'Anytime', icon: Sunset },
];

export default function Today() {
  const { user } = useAuth();
  const { prefs, setPref } = useUserPrefs();
  const navigate = useNavigate();
  const [habits, setHabits] = useState([]);
  const [logsToday, setLogsToday] = useState({});
  const [allLogsByHabit, setAllLogsByHabit] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const date = todayISO();

  const refresh = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    const [{ data: habitsData, error: hErr }, { data: todayData }, { data: allData }] = await Promise.all([
      supabase.from('habits').select('*').is('archived_at', null).order('sort_order').order('created_at'),
      supabase.from('habit_logs').select('*').eq('log_date', date),
      supabase.from('habit_logs').select('*').order('log_date', { ascending: true }),
    ]);
    if (hErr) toast.error(hErr.message);

    const tMap = {};
    for (const l of todayData || []) tMap[l.habit_id] = l;
    setLogsToday(tMap);

    const byHabit = {};
    for (const l of allData || []) {
      if (!byHabit[l.habit_id]) byHabit[l.habit_id] = [];
      byHabit[l.habit_id].push(l);
    }
    setAllLogsByHabit(byHabit);
    setHabits(habitsData || []);
    setLoading(false);
  }, [user, date]);

  useEffect(() => { refresh(); }, [refresh]);

  // Listen for habit-logged events fired by the mark-done notification
  // action so the Today list flips to done without requiring a manual
  // refresh.
  useEffect(() => {
    const onLogged = () => { refresh(); };
    window.addEventListener('mfh:habit-logged', onLogged);
    return () => window.removeEventListener('mfh:habit-logged', onLogged);
  }, [refresh]);

  // Milestone email side-effect + push iOS widget snapshot whenever today's
  // state changes.
  useEffect(() => {
    for (const h of habits) {
      const logs = allLogsByHabit[h.id] || [];
      const { current } = calculateStreaks(h, logs);
      maybeFireMilestoneEmail(h.id, current);
    }
    // Build a small snapshot for the iOS Home-Screen widget.
    const scheduled = habits.filter((h) => isScheduledOn(h, new Date()));
    const items = scheduled.slice(0, 10).map((h) => {
      const l = logsToday[h.id];
      const completed = l ? (h.type === 'binary'
        ? l.status === 'done'
        : Number(l.value || 0) >= Number(h.target_value || 1)) : false;
      return {
        id: h.id, name: h.name, icon: h.icon, color: h.color,
        time_of_day: h.time_of_day, completed,
      };
    });
    const doneCount = items.filter((x) => x.completed).length;
    const q = quoteForDay(new Date());
    updateWidgetSnapshot({
      updatedAt: new Date().toISOString(),
      dateISO: date,
      loggedOut: false,
      style: prefs.widget_style || 'habits',
      done: doneCount,
      total: items.length,
      items,
      quote: q.text,
      quoteAuthor: q.author,
    });
  }, [allLogsByHabit, habits, logsToday, date, prefs.widget_style]);

  // Hotkey 'n' + offline-flush events
  useEffect(() => {
    const open = () => { setEditing(null); setShowForm(true); };
    const onFlushed = () => refresh();
    window.addEventListener('mfh:new-habit', open);
    window.addEventListener('mfh:queue-flushed', onFlushed);
    return () => {
      window.removeEventListener('mfh:new-habit', open);
      window.removeEventListener('mfh:queue-flushed', onFlushed);
    };
  }, [refresh]);

  const isCompleted = useCallback((h, l = logsToday[h.id]) => {
    if (!l) return false;
    if (h.type === 'binary') return l.status === 'done';
    return Number(l.value || 0) >= Number(h.target_value || 1);
  }, [logsToday]);

  const upsertLog = async (habit, patch) => {
    const existing = logsToday[habit.id];
    const payload = {
      habit_id: habit.id,
      user_id: user.id,
      log_date: date,
      status: patch.status,
      value: patch.value ?? existing?.value ?? 0,
      note: patch.note ?? existing?.note ?? null,
      mood: patch.mood ?? existing?.mood ?? null,
    };
    if (!isOnline()) {
      enqueueLogUpsert(payload);
      const optimistic = { ...(existing || { id: `tmp-${Date.now()}` }), ...payload };
      setLogsToday((m) => ({ ...m, [habit.id]: optimistic }));
      setAllLogsByHabit((m) => {
        const arr = (m[habit.id] || []).filter((l) => l.log_date !== date);
        return { ...m, [habit.id]: [...arr, optimistic].sort((a, b) => a.log_date.localeCompare(b.log_date)) };
      });
      toast.message('Queued — will sync when online');
      return;
    }
    if (existing && !String(existing.id).startsWith('tmp-')) {
      const { data, error } = await supabase.from('habit_logs').update(payload).eq('id', existing.id).select().maybeSingle();
      if (error) { toast.error(error.message); return; }
      setLogsToday((m) => ({ ...m, [habit.id]: data }));
      setAllLogsByHabit((m) => {
        const arr = (m[habit.id] || []).filter((l) => l.id !== existing.id);
        return { ...m, [habit.id]: [...arr, data].sort((a, b) => a.log_date.localeCompare(b.log_date)) };
      });
    } else {
      const { data, error } = await supabase.from('habit_logs').insert(payload).select().maybeSingle();
      if (error) { toast.error(error.message); return; }
      setLogsToday((m) => ({ ...m, [habit.id]: data }));
      setAllLogsByHabit((m) => ({ ...m, [habit.id]: [...(m[habit.id] || []), data] }));
    }
  };

  const submitHabit = async (form) => {
    if (editing?.id) {
      const { data, error } = await supabase.from('habits').update(form).eq('id', editing.id).select().maybeSingle();
      if (error) { toast.error(error.message); return; }
      setHabits((hs) => hs.map((h) => (h.id === data.id ? data : h)));
      toast.success('Habit updated');
    } else {
      // Place new habit at the end (highest sort_order + 1)
      const maxSort = habits.reduce((m, h) => Math.max(m, h.sort_order ?? 0), 0);
      const { data, error } = await supabase.from('habits').insert({ ...form, user_id: user.id, sort_order: maxSort + 1 }).select().maybeSingle();
      if (error) { toast.error(error.message); return; }
      setHabits((hs) => [...hs, data]);
      toast.success('Habit created');
    }
    setEditing(null);
    // Re-schedule iOS / Android local notifications immediately (don't wait
    // for the supabase_realtime fan-out which may not be enabled).
    rescheduleHabitReminders();
  };

  const archive = async (habit) => {
    const { error } = await supabase.from('habits').update({ archived_at: habit.archived_at ? null : new Date().toISOString() }).eq('id', habit.id);
    if (error) { toast.error(error.message); return; }
    setHabits((hs) => hs.filter((h) => h.id !== habit.id));
    toast.success(habit.archived_at ? 'Restored' : 'Archived');
    rescheduleHabitReminders();
  };

  const remove = async (habit) => {
    if (!window.confirm(`Delete "${habit.name}"? This removes all its history.`)) return;
    const { error } = await supabase.from('habits').delete().eq('id', habit.id);
    if (error) { toast.error(error.message); return; }
    setHabits((hs) => hs.filter((h) => h.id !== habit.id));
    toast.success('Deleted');
    rescheduleHabitReminders();
  };

  // Persist a new sort order for the given list of habit ids.
  const persistOrder = async (orderedIds) => {
    const updates = orderedIds.map((id, idx) => ({ id, sort_order: idx + 1 }));
    setHabits((prev) => {
      const map = new Map(prev.map((h) => [h.id, h]));
      const reordered = orderedIds.map((id, idx) => ({ ...map.get(id), sort_order: idx + 1 }));
      const remaining = prev.filter((h) => !orderedIds.includes(h.id));
      return [...reordered, ...remaining];
    });
    try {
      const { error } = await supabase.from('habits').upsert(updates);
      if (error && error.code) toast.error(error.message);
    } catch (e) {
      // supabase-js occasionally throws a benign "body stream already read"
      // TypeError on upsert responses; the DB write itself succeeded.
      console.warn('[mfh] reorder upsert non-fatal:', e?.message);
    }
  };

  const scheduledToday = useMemo(
    () => habits.filter((h) => isScheduledOn(h, new Date())),
    [habits],
  );

  // Apply user's completed-behavior preference.
  const visible = useMemo(() => {
    const unfinished = scheduledToday.filter((h) => !isCompleted(h));
    const completed = scheduledToday.filter((h) => isCompleted(h));
    if (prefs.today_completed_behavior === 'hide') return unfinished;
    return [...unfinished, ...completed]; // 'move' (default)
  }, [scheduledToday, isCompleted, prefs.today_completed_behavior]);

  // Group by time_of_day but preserve overall ordering within groups.
  const grouped = useMemo(() => {
    return GROUPS.map((g) => ({ ...g, items: visible.filter((h) => h.time_of_day === g.key) }))
                 .filter((g) => g.items.length > 0);
  }, [visible]);

  const total = scheduledToday.length;
  const done = scheduledToday.filter((h) => isCompleted(h)).length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);

  const toggleCompletedBehavior = () => {
    const next = prefs.today_completed_behavior === 'hide' ? 'move' : 'hide';
    setPref('today_completed_behavior', next);
    toast.message(next === 'hide' ? 'Hiding completed' : 'Moving completed to bottom');
  };

  const switchToWelcome = async () => {
    await setPref('home_screen', 'welcome');
    navigate('/home', { replace: true });
  };

  return (
    <div className="space-y-8" data-testid="today-page">
      <WidgetCoach />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">Today</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight mt-1">{fmtPretty(new Date())}</h1>
          <p className="text-sm text-muted-foreground mt-2" data-testid="today-progress">
            {total === 0 ? 'No habits scheduled today' : `${done} of ${total} done · ${pct}%`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleCompletedBehavior}
            className="text-xs text-muted-foreground hover:text-foreground rounded-full px-3 py-1.5 hover:bg-muted transition-colors inline-flex items-center gap-1.5"
            data-testid="today-completed-toggle"
            title={prefs.today_completed_behavior === 'hide' ? 'Currently: hide completed' : 'Currently: move completed to bottom'}
          >
            {prefs.today_completed_behavior === 'hide' ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {prefs.today_completed_behavior === 'hide' ? 'Hidden' : 'Moved'}
          </button>
          <button
            type="button"
            onClick={switchToWelcome}
            className="text-xs text-muted-foreground hover:text-foreground rounded-full px-3 py-1.5 hover:bg-muted transition-colors inline-flex items-center gap-1.5"
            data-testid="today-switch-to-welcome"
            title="Switch to Welcome dashboard"
          >
            <LayoutDashboard className="h-3.5 w-3.5" /> Welcome
          </button>
          <Button onClick={() => { setEditing(null); setShowForm(true); }} className="rounded-full" data-testid="new-habit-btn">
            <Plus className="h-4 w-4" /> New habit
          </Button>
        </div>
      </div>

      {/* progress bar */}
      {total > 0 && (
        <div className="h-1.5 rounded-full bg-muted overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} aria-hidden />
        </div>
      )}

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => <Skeleton key={`sk-${i}`} className="h-16 w-full rounded-xl" />)}
        </div>
      ) : habits.length === 0 ? (
        <EmptyState
          icon={ListTodo}
          title="Start your first habit"
          description="Pick from templates, or design your own. Small steps, big flow."
          action={
            <div className="flex gap-2">
              <Button onClick={() => { setEditing(null); setShowForm(true); }} className="rounded-full" data-testid="empty-new-habit-btn">
                <Plus className="h-4 w-4" /> Create habit
              </Button>
              <Button asChild variant="ghost" className="rounded-full">
                <Link to="/templates" data-testid="empty-templates-link">Browse templates</Link>
              </Button>
            </div>
          }
          testid="today-empty"
        />
      ) : grouped.length === 0 ? (
        <EmptyState icon={Sparkles} title={prefs.today_completed_behavior === 'hide' ? 'All done for today' : 'Rest day'} description={prefs.today_completed_behavior === 'hide' ? 'Great job. Enjoy the calm.' : 'No habits scheduled today. Enjoy the calm.'} testid="today-rest-empty" />
      ) : (
        <div className="space-y-10">
          {grouped.map((g) => {
            // Only unfinished items are reorderable; completed are pinned at bottom.
            const unfinishedIds = g.items.filter((h) => !isCompleted(h)).map((h) => h.id);
            const completedItems = g.items.filter((h) => isCompleted(h));
            return (
              <section key={g.key} data-testid={`group-${g.key}`}>
                <div className="flex items-center gap-2 mb-2 px-2 sm:px-4">
                  <g.icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.6} />
                  <h2 className="uppercase tracking-[0.2em] text-xs text-muted-foreground font-medium">{g.label}</h2>
                </div>
                <div className="rounded-2xl border border-border bg-card/30 overflow-hidden">
                  {unfinishedIds.length > 0 && (
                    <SortableList items={unfinishedIds} onReorder={persistOrder}>
                      {unfinishedIds.map((id) => {
                        const h = g.items.find((x) => x.id === id);
                        return (
                          <SortableRow key={h.id} id={h.id} testid={`sortable-${h.id}`}>
                            <HabitItem
                              habit={h}
                              log={logsToday[h.id]}
                              streak={calculateStreaks(h, allLogsByHabit[h.id] || []).current}
                              onLog={(patch) => upsertLog(h, patch)}
                              onEdit={(habit) => { setEditing(habit); setShowForm(true); }}
                              onArchive={archive}
                              onDelete={remove}
                            />
                          </SortableRow>
                        );
                      })}
                    </SortableList>
                  )}
                  {completedItems.map((h) => (
                    <HabitItem
                      key={h.id}
                      habit={h}
                      log={logsToday[h.id]}
                      streak={calculateStreaks(h, allLogsByHabit[h.id] || []).current}
                      onLog={(patch) => upsertLog(h, patch)}
                      onEdit={(habit) => { setEditing(habit); setShowForm(true); }}
                      onArchive={archive}
                      onDelete={remove}
                    />
                  ))}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <HabitFormModal open={showForm} onOpenChange={(o) => { setShowForm(o); if (!o) setEditing(null); }} initial={editing} onSubmit={submitHabit} />
    </div>
  );
}
